import { CLIENT_ID, SCOPES, DISCOVERY_DOCS } from './config';

let tokenClient = null;
let gapiLoaded = false;
let gsiLoaded = false;

export function isConfigured() {
  return CLIENT_ID && CLIENT_ID !== 'YOUR_CLIENT_ID_HERE';
}

export function loadGapiScript() {
  return new Promise((resolve) => {
    if (gapiLoaded) return resolve();
    const script = document.createElement('script');
    script.src = 'https://apis.google.com/js/api.js';
    script.onload = () => {
      window.gapi.load('client', async () => {
        await window.gapi.client.init({});
        for (const doc of DISCOVERY_DOCS) {
          try {
            await window.gapi.client.load(doc);
          } catch (e) {
            console.warn('Failed to load discovery doc:', doc, e);
          }
        }
        gapiLoaded = true;
        resolve();
      });
    };
    document.body.appendChild(script);
  });
}

export function loadGsiScript() {
  return new Promise((resolve) => {
    if (gsiLoaded) return resolve();
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.onload = () => {
      gsiLoaded = true;
      resolve();
    };
    document.body.appendChild(script);
  });
}

export async function initAuth() {
  if (!isConfigured()) return null;
  await Promise.all([loadGapiScript(), loadGsiScript()]);
  return true;
}

function makeTokenClient(onSuccess, onError) {
  return window.google.accounts.oauth2.initTokenClient({
    client_id: CLIENT_ID,
    scope: SCOPES,
    callback: (response) => {
      if (response.error) {
        onError && onError(response);
        return;
      }
      // Store token and expiry — localStorage so it survives across tabs/sessions
      const token = {
        access_token: response.access_token,
        expires_at: Date.now() + (response.expires_in * 1000),
      };
      localStorage.setItem('harmonee_token', JSON.stringify(token));
      onSuccess && onSuccess(token);
    },
  });
}

// ──── Multi-account store ────
// Several business Gmail accounts can be connected at once. Each entry:
// { email, name, firstName, initials, picture, access_token, expires_at }
// The FIRST entry is the primary account (greeting, default sender).
const ACCOUNTS_KEY = 'harmonee_accounts_v1';

export function getAccounts() {
  try {
    return JSON.parse(localStorage.getItem(ACCOUNTS_KEY)) || [];
  } catch {
    return [];
  }
}

function saveAccounts(accounts) {
  localStorage.setItem(ACCOUNTS_KEY, JSON.stringify(accounts));
}

export function upsertAccount(account) {
  if (!account?.email) return getAccounts();
  const accounts = getAccounts();
  const i = accounts.findIndex((a) => a.email === account.email);
  if (i >= 0) accounts[i] = { ...accounts[i], ...account };
  else accounts.push(account);
  saveAccounts(accounts);
  return accounts;
}

export function removeAccount(email) {
  const accounts = getAccounts();
  const target = accounts.find((a) => a.email === email);
  if (target?.access_token) {
    try { window.google?.accounts?.oauth2?.revoke(target.access_token); } catch { /* revoked or offline */ }
  }
  const next = accounts.filter((a) => a.email !== email);
  saveAccounts(next);
  return next;
}

export function getPrimaryAccount() {
  return getAccounts()[0] || null;
}

// Token for one account, or null if missing/expired.
export function getTokenFor(email) {
  const account = getAccounts().find((a) => a.email === email);
  if (!account?.access_token) return null;
  if (Date.now() > account.expires_at) return null;
  return { access_token: account.access_token, expires_at: account.expires_at };
}

// One-time migration: fold the old single-account keys into the store.
// Pass the cached profile (from 'harmonee_user') if the caller has it.
export function migrateLegacyAccount(profile) {
  if (getAccounts().length > 0) return getAccounts();
  const tokenStr = localStorage.getItem('harmonee_token');
  if (!tokenStr || !profile?.email) return getAccounts();
  try {
    const token = JSON.parse(tokenStr);
    return upsertAccount({ ...profile, ...token });
  } catch {
    return getAccounts();
  }
}

// First-time sign in — full consent screen
export function signIn(onSuccess, onError) {
  tokenClient = makeTokenClient(onSuccess, onError);
  tokenClient.requestAccessToken({ prompt: 'consent' });
}

// Add ANOTHER Google account: forces the account chooser so Google doesn't
// silently reuse the current session. The caller must identify the account
// (userinfo with this token) and upsertAccount() it — the token alone
// doesn't say which inbox it belongs to.
export function addGoogleAccount(onSuccess, onError) {
  const client = window.google.accounts.oauth2.initTokenClient({
    client_id: CLIENT_ID,
    scope: SCOPES,
    callback: (response) => {
      if (response.error) {
        onError && onError(response);
        return;
      }
      onSuccess && onSuccess({
        access_token: response.access_token,
        expires_at: Date.now() + (response.expires_in * 1000),
      });
    },
    error_callback: (err) => onError && onError(err),
  });
  client.requestAccessToken({ prompt: 'select_account' });
}

// Silent re-auth — no popup, only works if user previously consented.
// With an email, refreshes THAT account (login_hint); without, legacy behavior.
export function signInSilent(email) {
  return new Promise((resolve, reject) => {
    if (!window.google?.accounts?.oauth2) return reject(new Error('GSI not ready'));
    const client = window.google.accounts.oauth2.initTokenClient({
      client_id: CLIENT_ID,
      scope: SCOPES,
      callback: (response) => {
        if (response.error) {
          reject(response);
          return;
        }
        const token = {
          access_token: response.access_token,
          expires_at: Date.now() + (response.expires_in * 1000),
        };
        if (email) upsertAccount({ email, ...token });
        else localStorage.setItem('harmonee_token', JSON.stringify(token));
        resolve(token);
      },
      error_callback: (err) => reject(err),
    });
    // prompt: '' = no UI shown, silent refresh
    client.requestAccessToken(email ? { prompt: '', login_hint: email } : { prompt: '' });
  });
}

export function signOut() {
  // Revoke every connected account plus the legacy single token.
  for (const account of getAccounts()) {
    if (account.access_token) {
      try { window.google.accounts.oauth2.revoke(account.access_token); } catch { /* already gone */ }
    }
  }
  const tokenStr = localStorage.getItem('harmonee_token');
  if (tokenStr) {
    try { window.google.accounts.oauth2.revoke(JSON.parse(tokenStr).access_token); } catch { /* already gone */ }
  }
  localStorage.removeItem('harmonee_token');
  localStorage.removeItem(ACCOUNTS_KEY);
}

export function getStoredToken() {
  // Primary account first; fall back to the legacy single-token key.
  const primary = getPrimaryAccount();
  if (primary) {
    return getTokenFor(primary.email);
  }
  const tokenStr = localStorage.getItem('harmonee_token');
  if (!tokenStr) return null;
  const token = JSON.parse(tokenStr);
  if (Date.now() > token.expires_at) {
    localStorage.removeItem('harmonee_token');
    return null;
  }
  return token;
}

export function setGapiToken(token) {
  if (token && window.gapi?.client) {
    window.gapi.client.setToken({ access_token: token.access_token });
  }
}
